import { ApiShipmentType } from "entities/shared/shipmentCard.types";
import { backofficeClient } from "shared/api/backofficeClient";

import type {
  AirWhereQuery,
  FollowCardQuery,
  GetCardsQuery,
  UpdateCardData,
  UpdateCardQuery,
  UpdateCardsQuery,
} from "./query.types";
import type {
  DeleteCardResponseDTO,
  SavedAirResponseDataDTO,
  SavedAirResponseDTO,
  UpdateCardsResponseDTO,
} from "./response.types";

/**
 * Saved waybills live in the back office and are authenticated by the session
 * cookie, on the same /api/shipments endpoints as the ocean cards: one table
 * holds both. `shipment_type` is what keeps the two lists apart, so it is
 * pinned here rather than left to each caller - a read that forgot it would
 * quietly show ocean cards in the air panel.
 */
const userRequestInstance = backofficeClient;

const airOnly = (where?: AirWhereQuery): AirWhereQuery => ({
  ...where,
  shipment_type: ApiShipmentType.AIR,
});

class SavedShipmentApi {
  public getCards(body?: GetCardsQuery, signal?: AbortSignal): Promise<SavedAirResponseDTO> {
    const defaultParams: Partial<GetCardsQuery> = {
      limit: null,
      offset: null,
      order: [{ column: "created_at", asc: false }],
    };
    const data: GetCardsQuery = {
      ...defaultParams,
      ...body,
      where: airOnly(body?.where),
      order: [...(body?.order ?? []), ...(defaultParams.order ?? [])],
    };
    return userRequestInstance.post<SavedAirResponseDTO, SavedAirResponseDTO, GetCardsQuery>(
      "/api/shipments/get",
      data,
      { ...(signal ? { signal } : {}) }
    );
  }

  public updateCard({ id, data }: UpdateCardQuery): Promise<SavedAirResponseDataDTO> {
    return userRequestInstance.put<SavedAirResponseDataDTO, SavedAirResponseDataDTO, UpdateCardData>(
      `/api/shipments/${id}`,
      data
    );
  }

  public updateCards(data: UpdateCardsQuery): Promise<UpdateCardsResponseDTO> {
    return userRequestInstance.post<UpdateCardsResponseDTO, UpdateCardsResponseDTO, UpdateCardsQuery>(
      "/api/shipments/update",
      { ...data, where: airOnly(data.where) }
    );
  }

  public deleteCard(id: number): Promise<DeleteCardResponseDTO> {
    return userRequestInstance.delete<DeleteCardResponseDTO, DeleteCardResponseDTO>(`/api/shipments/${id}`);
  }

  public followCard(data: FollowCardQuery): Promise<SavedAirResponseDataDTO> {
    return userRequestInstance.post<SavedAirResponseDataDTO, SavedAirResponseDataDTO, FollowCardQuery>(
      "/api/shipments",
      data
    );
  }
}

const savedShipmentService = new SavedShipmentApi();

export { savedShipmentService };
